/**
 * The SnackBar provider manages snackbar display based on the meta.snackBar response
 *
 * @module views/components/AriaLiveRegionProvider
 * @memberof -Common
 */
import React, {
  createContext,
  useCallback, useContext,
  useMemo,
  useState } from 'react';

import { useTimeout } from '@ulta/core/hooks/useTimeout/useTimeout';

export const ARIA_LIVE_LOCATION = {
  Overlay: 'overlay',
  Page: 'page'
};

/**
 * Represents a AriaLiveRegionProvider component
 *
 * @method
 * @param {object} props - React properties passed from composition
 * @returns AriaLiveRegionProvider
 */
export const AriaLiveRegionProvider = ( props ) => {
  const [announcements, setAnnouncements] = useState( [] );

  const [removeAnnouncementTimeout] = useTimeout();

  const broadcastMessage = useCallback(
    ( announcement ) => composeBroadcastMessage( { announcement }, { setAnnouncements, removeAnnouncementTimeout } ),
    [setAnnouncements, removeAnnouncementTimeout]
  );

  return (
    <AriaLiveRegionContext.Provider
      value={ {
        announcements,
        broadcastMessage
      } }
    >
      { props.children }
    </AriaLiveRegionContext.Provider>
  );
};

/**
* handles the aria announcement
* @method
* @param {Object} data - Passing required data as an argument
* @param {Object} methods - Passing announcement methods as an argument
*/
export const composeBroadcastMessage = ( data, methods ) => {
  const { setAnnouncements, removeAnnouncementTimeout } = methods || {};
  const { announcement } = data || {};

  if( !announcement || !setAnnouncements || !removeAnnouncementTimeout ){
    return;
  }

  const { id, message, delay = 60000, location = ARIA_LIVE_LOCATION.Page } = announcement;
  setAnnouncements( ( prev ) => ( [...prev, {
    id,
    message,
    location
  }] ) );
  removeAnnouncementTimeout(
    () => {
      setAnnouncements( ( prevAnnouncements ) => prevAnnouncements.filter( ( _announcement ) => _announcement.id !== id ) );
    },
    delay
  );
};

export const AriaLiveRegionContext = createContext( {} );

export const useAriaLiveRegion = () => useContext( AriaLiveRegionContext );

export const AriaLiveRegion = ( { location = ARIA_LIVE_LOCATION.Page } ) => {
  const { announcements = [] } = useAriaLiveRegion();

  const filteredAnnouncements = useMemo( () => announcements.filter( ( announcement ) => announcement.location === location ), [announcements] );

  return (
    <div className={ `sr-only aria-live-region ${location}-announcements` }
      aria-atomic='true'
      aria-live='polite'
    >
      { filteredAnnouncements.map( ( { id, message } ) => (
        <p key={ id }
          role='alert'
          id={ id }
        >{ message }</p>
      ) ) }
    </div>
  );
};

export default AriaLiveRegionProvider;